import {
  loadingExportFile,
  succeededExportFile,
  failedExportFile,
  idleExportFile,
  Status,
  showModalExportFavoris,
} from 'redux/features/favoris/slice'
import {
  makeSelectExportFileBlob,
  makeSelectStatusExportFile,
  makeSelectErrorExportFile,
  makeSelectFavoris,
} from 'redux/features/favoris/selectors'
import { useAppSelector, useAppDispatch } from 'redux/hooks'
import { fetchWithCheckAuthorization } from 'utils/fetchWithAuthorization'
import { useEffect } from 'react'

interface ReturnProps {
  show: boolean
  loading: boolean
  error: string | null
  showModal: () => void
  exportSelectionToPdf: () => void
  exportSelectionToEmail: () => void
  cancelExport: () => void
  countFavorisChecked: number
}

const useExportFavoris = (): ReturnProps => {
  const favoris = useAppSelector(makeSelectFavoris)
  const exportFileBlob = useAppSelector(makeSelectExportFileBlob)
  const statusExportFile = useAppSelector(makeSelectStatusExportFile)
  const errorExportFile = useAppSelector(makeSelectErrorExportFile)
  const dispatch = useAppDispatch()

  const exportSelectionToPdf = async () => {
    dispatch(loadingExportFile())
    const selection = Object.values(favoris).filter(
      ({ isChecked }) => isChecked
    )
    const selectionUid = selection.map(({ uidParcelle }) => uidParcelle)
    try {
      const response = await fetchWithCheckAuthorization(
        '/api/favoris/export',
        {
          method: 'POST',
          body: JSON.stringify(selectionUid),
        }
      )
      if (response.ok && response.status === 200) {
        const blob = await response.blob()
        dispatch(succeededExportFile(blob))
        return
      }
      switch (response.status) {
        case 401:
          throw Error(response.statusText)
        case 500:
          throw Error('Erreur interne')
        default:
          throw Error('Une erreur est survenue')
      }
    } catch (err: unknown) {
      console.error(err)
      dispatch(failedExportFile((err as Error).toString()))
    }
  }

  // API ROUTE NOT YET AVAILABLE
  const exportSelectionToEmail = async () => null

  useEffect(() => {
    const downloadPdf = (fileBlob: Blob) => {
      const href = window.URL.createObjectURL(fileBlob)
      const link = document.createElement('a')
      link.href = href
      link.setAttribute(
        'download',
        `favoris_${new Date().toJSON().slice(0, 10)}.html`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    if (statusExportFile === Status.Succeeded && exportFileBlob !== null) {
      downloadPdf(exportFileBlob)
      dispatch(idleExportFile())
    }
  }, [statusExportFile, exportFileBlob, dispatch])

  const showModal = () => {
    dispatch(showModalExportFavoris())
  }

  const cancelExport = () => dispatch(idleExportFile())

  return {
    show:
      statusExportFile === Status.ShowModalExportFavoris ||
      statusExportFile === Status.Pending ||
      statusExportFile === Status.Failed,
    loading: statusExportFile === Status.Pending,
    error: (statusExportFile === Status.Failed && errorExportFile) || null,
    showModal,
    exportSelectionToPdf,
    exportSelectionToEmail,
    cancelExport,
    countFavorisChecked: Object.values(favoris).filter(
      ({ isChecked }) => isChecked
    ).length,
  }
}

export default useExportFavoris
